package top.wcpe.allincore.ui.runtime.ir.api

import top.wcpe.allincore.ui.runtime.nbt.NbtCompound
import top.wcpe.allincore.ui.runtime.nbt.emptyNbtCompound
import top.wcpe.allincore.ui.runtime.nbt.nbtBinaryRead
import top.wcpe.allincore.ui.runtime.nbt.nbtViewsDisplayName
import top.wcpe.allincore.ui.runtime.nbt.nbtViewsEnchanted
import top.wcpe.allincore.ui.runtime.nbt.nbtViewsLore
import top.wcpe.allincore.ui.runtime.protocol.ItemStackViewWire
import top.wcpe.allincore.ui.runtime.protocol.itemStackDefaultMaxStack
import top.wcpe.allincore.ui.runtime.protocol.itemStackNoDurability
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/** 客户端使用的物品快照。 */
class ClientItemStack(
    private val itemId: String,
    private val count: Int,
    private val durability: Float?,
    private val cooldown: Float,
    private val nbt: NbtCompound,
    private val maxStackSize: Int,
) {
    constructor(itemId: String, count: Int, durability: Float?, cooldown: Float, nbt: NbtCompound) :
        this(itemId, count, durability, cooldown, nbt, DEFAULT_MAX_STACK)

    companion object {
        /** 默认堆叠上限（真源 ItemStackViewWire.DEFAULT_MAX_STACK；原 const 降为 @JvmField val——common 不能引用 Java 常量作 const 初始器，Java 调用点的静态字段访问形态不变）。 */
        @JvmField val DEFAULT_MAX_STACK: Int = itemStackDefaultMaxStack

        @JvmField val EMPTY: ClientItemStack = ClientItemStack("", 0, null, 0f, emptyNbtCompound, DEFAULT_MAX_STACK)

        @JvmStatic
        fun fromWire(wire: ItemStackViewWire?): ClientItemStack {
            if (wire == null) return EMPTY
            val itemId = wire.getItemId()
            if (itemId.isNullOrEmpty() || wire.getCount() <= 0) return EMPTY
            val nbtBytes = wire.getNbtBytes()
            val parsed = if (nbtBytes == null || nbtBytes.isEmpty()) null else nbtBinaryRead(nbtBytes)
            val nbt: NbtCompound = if (parsed is NbtCompound) parsed else emptyNbtCompound
            val durability = if (wire.getDurability() == itemStackNoDurability) null else wire.getDurability()
            val maxStack = if (wire.getMaxStackSize() > 0) wire.getMaxStackSize() else DEFAULT_MAX_STACK
            return ClientItemStack(itemId, wire.getCount(), durability, wire.getCooldown(), nbt, maxStack)
        }
    }

    fun itemId(): String = itemId
    fun count(): Int = count
    fun durability(): Float? = durability
    fun cooldown(): Float = cooldown
    fun nbt(): NbtCompound = nbt
    fun maxStackSize(): Int = maxStackSize

    fun isEmpty(): Boolean = count <= 0 || itemId.isEmpty()

    fun displayName(): String? {
        val name = nbtViewsDisplayName(nbt)
        return name.ifEmpty { null }
    }

    fun lore(): List<String> = nbtViewsLore(nbt)
    fun enchanted(): Boolean = nbtViewsEnchanted(nbt)

    fun withCount(newCount: Int): ClientItemStack =
        if (newCount == count) this else ClientItemStack(itemId, newCount, durability, cooldown, nbt, maxStackSize)

    fun withDurability(newDurability: Float?): ClientItemStack = if (newDurability == durability) {
        this
    } else {
        ClientItemStack(itemId, count, newDurability, cooldown, nbt, maxStackSize)
    }

    fun withCooldown(newCooldown: Float): ClientItemStack = if (newCooldown == cooldown) {
        this
    } else {
        ClientItemStack(itemId, count, durability, newCooldown, nbt, maxStackSize)
    }

    fun withNbt(newNbt: NbtCompound?): ClientItemStack {
        val target = newNbt ?: emptyNbtCompound
        return if (target == nbt) this else ClientItemStack(itemId, count, durability, cooldown, target, maxStackSize)
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is ClientItemStack) return false
        return count == other.count &&
            other.cooldown.compareTo(cooldown) == 0 &&
            maxStackSize == other.maxStackSize &&
            itemId == other.itemId &&
            durability == other.durability &&
            nbt == other.nbt
    }

    // List.hashCode 与 java.util.Objects.hash 同算法（1 起 31 逐项组合），迁移前后散列值一致
    override fun hashCode(): Int = listOf(itemId, count, durability, cooldown, nbt, maxStackSize).hashCode()

    override fun toString(): String = "ClientItemStack{${'$'}{itemId}x${'$'}count}"
}
